import { Landmark } from "lucide-react";
import { useLang } from "@/lib/lang-context";

/**
 * Annual IMU estimate block. Lives right under the AcquisitionCost +
 * CondoFees section because all three answer the "what does owning this
 * actually cost me?" question.
 *
 * Hidden when the backend returns null (agricultural land or missing price).
 */
const fmtEur = (n) =>
  n == null
    ? "—"
    : new Intl.NumberFormat("it-IT", {
        style: "currency",
        currency: "EUR",
        maximumFractionDigits: 0,
      }).format(n);

const ImuEstimate = ({ estimate }) => {
  const { t } = useLang();
  if (!estimate) return null;
  return (
    <section
      data-testid="imu-estimate"
      className="px-6 md:px-10 py-6 border-t border-[#E6E4DD]"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="label-eyebrow flex items-center gap-2">
            <Landmark size={12} strokeWidth={1.5} />
            {t("imu.eyebrow")}
          </div>
          <h3 className="font-serif-display text-2xl mt-2 text-[#282C2A]">
            {t("imu.title")}
          </h3>
        </div>
        <span
          data-testid="imu-rate"
          className="font-mono text-[10px] uppercase tracking-widest px-2 py-1 bg-[#F2EFE6] border border-[#E6E4DD] text-[#6C706D] self-start"
        >
          {estimate.rate_pct.toFixed(2)}% {t("imu.aliquota")}
        </span>
      </div>

      <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div>
          <div className="label-eyebrow">{t("imu.annual")}</div>
          <div
            data-testid="imu-annual"
            className="font-mono text-xl mt-2 text-[#282C2A]"
          >
            {fmtEur(estimate.annual_min_eur)}–{fmtEur(estimate.annual_max_eur)}
          </div>
        </div>
        <div>
          <div className="label-eyebrow">{t("imu.valoreCatastale")}</div>
          <div
            data-testid="imu-valore"
            className="font-mono text-xl mt-2 text-[#282C2A]"
          >
            {fmtEur(estimate.valore_catastale_min_eur)}–
            {fmtEur(estimate.valore_catastale_max_eur)}
          </div>
        </div>
      </div>

      {estimate.assumptions?.length > 0 && (
        <ul
          data-testid="imu-assumptions"
          className="mt-4 text-xs text-[#6C706D] space-y-1"
        >
          {estimate.assumptions.map((a, i) => (
            <li key={i} className="flex items-start gap-2">
              <span className="text-[#A0A39B]">·</span>
              <span>{a}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default ImuEstimate;
